const DEFAULT_RBI_WEIGHTS = {
  visibility: 0.35,
  sentiment: 0.35,
  brandPillars: 0.15,
  purchaseCriteria: 0.15,
};

// Employer index: every factor counts the same.
const EQUAL_RBI_WEIGHTS = {
  visibility: 0.25,
  sentiment: 0.25,
  brandPillars: 0.25,
  purchaseCriteria: 0.25,
};

// Users who see the equal 25% weights (matches the Employer Discovery report).
const EQUAL_WEIGHT_USER_IDS = new Set([238]);

function getLoggedInUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const id = user?.user_role?.user_id;
    return id == null ? null : Number(id);
  } catch {
    return null;
  }
}

function resolveRbiWeights() {
  return EQUAL_WEIGHT_USER_IDS.has(getLoggedInUserId()) ? EQUAL_RBI_WEIGHTS : DEFAULT_RBI_WEIGHTS;
}

// A Proxy so the weights resolve on every read (like ENTITY_LABELS): they follow whoever is logged in,
// with no stale value from import time.
export const RBI_WEIGHTS = new Proxy(DEFAULT_RBI_WEIGHTS, {
  get: (_, key) => resolveRbiWeights()[key],
  ownKeys: () => Reflect.ownKeys(DEFAULT_RBI_WEIGHTS),
  getOwnPropertyDescriptor: (_, key) =>
    key in DEFAULT_RBI_WEIGHTS
      ? { value: resolveRbiWeights()[key], enumerable: true, configurable: true, writable: false }
      : undefined,
});

/** "35%" / "25%" label for a weight key of RBI_WEIGHTS. */
export function rbiWeightLabel(key) {
  return `${Math.round((RBI_WEIGHTS[key] ?? 0) * 100)}%`;
}

export const RBI_COLORS = {
  visibility: "#5b8dee",      // soft cornflower blue
  sentiment: "#e8a838",       // warm amber
  brandPillars: "#9b7dd4",    // soft lavender
  purchaseCriteria: "#3dba8c", // muted teal
};

/**
 * Normalize raw scores to 0-100 scale.
 * rank/favorability are 0-50 → ×2
 * brandPillarsAvg/purchaseCriteriaAvg are 0-10 → ×10
 * Null/undefined inputs normalize to 0.
 */
export function normalizeScores({ rank, favorability, brandPillarsAvg, purchaseCriteriaAvg }) {
  const visNorm = Math.min(100, Math.max(0, (rank ?? 0) * 2));
  const sentNorm = Math.min(100, Math.max(0, (favorability ?? 0) * 2));
  const pillarsNorm = Math.min(100, Math.max(0, (brandPillarsAvg ?? 0) * 10));
  const purchaseNorm = Math.min(100, Math.max(0, (purchaseCriteriaAvg ?? 0) * 10));
  return { visNorm, sentNorm, pillarsNorm, purchaseNorm };
}

/**
 * Compute the weighted Brand Index Score.
 * Returns { total, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions }
 */
export function calcBrandIndexScore({ rank, favorability, brandPillarsAvg, purchaseCriteriaAvg }) {
  const { visNorm, sentNorm, pillarsNorm, purchaseNorm } = normalizeScores({
    rank, favorability, brandPillarsAvg, purchaseCriteriaAvg,
  });

  const contributions = {
    visibility: visNorm * RBI_WEIGHTS.visibility,
    sentiment: sentNorm * RBI_WEIGHTS.sentiment,
    brandPillars: pillarsNorm * RBI_WEIGHTS.brandPillars,
    purchaseCriteria: purchaseNorm * RBI_WEIGHTS.purchaseCriteria,
  };

  const total = Math.round(
    contributions.visibility +
    contributions.sentiment +
    contributions.brandPillars +
    contributions.purchaseCriteria
  );

  return { total, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions };
}

export const ACCESSIBILITY_INDEX_SCORE_TYPE = "accessibility";

/** Case-insensitive check for the "Accessibility" custom index score type. */
export function isAccessibilityIndexScoreType(customIndexScoreType) {
  return (customIndexScoreType || "").toString().toLowerCase() === ACCESSIBILITY_INDEX_SCORE_TYPE;
}

export const ACCESSIBILITY_RBI_WEIGHTS = {
  visibility: 1 / 3,
  sentiment: 1 / 3,
  brandPillars: 1 / 3,
};

export const ACCESSIBILITY_RBI_COLORS = {
  visibility: RBI_COLORS.visibility,
  sentiment: RBI_COLORS.sentiment,
  brandPillars: RBI_COLORS.brandPillars,
};

// Cycled by index to color each factor card/segment when the Accessibility
// UI is grouped factor-first (one color per factor, not per metric).
export const FACTOR_COLORS = [
  RBI_COLORS.visibility,
  RBI_COLORS.sentiment,
  RBI_COLORS.brandPillars,
  RBI_COLORS.purchaseCriteria,
  "#e07a9e", // rose
  "#6fa8a0", // sage
];

export function getFactorColor(index) {
  return FACTOR_COLORS[index % FACTOR_COLORS.length];
}


export function normalizeFactorScores({ rank, sentiment, pillar, perFactorMax }) {
  const visNorm = Math.min(perFactorMax, Math.max(0, ((rank ?? 0) / 50) * perFactorMax));
  const sentNorm = Math.min(perFactorMax, Math.max(0, ((sentiment ?? 0) / 50) * perFactorMax));
  const pillarsNorm = Math.min(perFactorMax, Math.max(0, ((pillar ?? 0) / 10) * perFactorMax));
  return { visNorm, sentNorm, pillarsNorm };
}

export function calcAccessibilityIndexScore({ rank, favorability, brandPillarsAvg, perFactorMax = 25 }) {
  const { visNorm, sentNorm, pillarsNorm } = normalizeFactorScores({
    rank, sentiment: favorability, pillar: brandPillarsAvg, perFactorMax,
  });

  const contributions = {
    visibility: visNorm * ACCESSIBILITY_RBI_WEIGHTS.visibility,
    sentiment: sentNorm * ACCESSIBILITY_RBI_WEIGHTS.sentiment,
    brandPillars: pillarsNorm * ACCESSIBILITY_RBI_WEIGHTS.brandPillars,
  };

  const total = contributions.visibility + contributions.sentiment + contributions.brandPillars;

  return { total, visNorm, sentNorm, pillarsNorm, contributions };
}

/**
 * @param {string[]} factors - factor names, e.g. Object.keys(sentimentScoreByFactor)
 * @param {(factor: string) => number|null} getRankForFactor - that factor's own visibility/rank score (0-50)
 * @param {(factor: string) => number|null} getSentimentForFactor - that factor's sentiment score (0-50)
 * @param {(factor: string) => number|null} getPillarForFactor - that factor's own pillar score (0-10)
 */
export function calcMultiFactorIndexScore({ factors, getRankForFactor, getSentimentForFactor, getPillarForFactor }) {
  const perFactorMax = factors.length ? 100 / factors.length : 0;

  const breakdown = factors.map((factor) => {
    const rank = getRankForFactor ? getRankForFactor(factor) : null;
    const sentiment = getSentimentForFactor ? getSentimentForFactor(factor) : null;
    const pillar = getPillarForFactor ? getPillarForFactor(factor) : null;
    const { total, visNorm, sentNorm, pillarsNorm, contributions } = calcAccessibilityIndexScore({
      rank, favorability: sentiment, brandPillarsAvg: pillar, perFactorMax,
    });
    return {
      factor,
      perFactorMax,
      visRaw: rank,
      sentRaw: sentiment,
      pillarRaw: pillar,
      visNorm, sentNorm, pillarsNorm, contributions,
      total,
    };
  });

  const total = Math.round(breakdown.reduce((sum, f) => sum + f.total, 0));
  return { total, perFactorMax, breakdown };
}

export const ACCESSIBILITY_METRIC_KEYS = ["visibility", "sentiment", "brandPillars"];

/**
 * Orders accessibility factors to match the sequence their visibility
 * datasets were actually run in (productMappingAttribute's key order —
 * e.g. set1-5 → first factor, set6-12 → second, ...), instead of whatever
 * incidental order Object.keys(sentimentScoreByFactor) happens to yield.
 * Falls back to the sentiment-derived order when no mapping is available.
 */
export function getOrderedAccessibilityFactors(sentimentScoreByFactor, productMappingAttribute) {
  const sentimentFactors = sentimentScoreByFactor ? Object.keys(sentimentScoreByFactor) : [];
  if (!sentimentFactors.length) return [];

  const mappingOrder = productMappingAttribute ? Object.keys(productMappingAttribute) : [];
  if (!mappingOrder.length) return sentimentFactors;

  const ordered = mappingOrder.filter((f) => sentimentFactors.includes(f));
  const remaining = sentimentFactors.filter((f) => !ordered.includes(f));
  return [...ordered, ...remaining];
}




export const COMPOSITE_INDEX_SCORE_TYPE = "direct_and_composite";

/** Case-insensitive check for the "Direct and Composite" custom index score type. */
export function isCompositeIndexScoreType(customIndexScoreType) {
  return (customIndexScoreType || "").toString().toLowerCase() === COMPOSITE_INDEX_SCORE_TYPE;
}

export function isExcludedCompositeFactor(factor) {
  const name = (factor || "").toString().toLowerCase();
  return name.includes("overall") && name.includes("place to work");
}

/** Filters a factor list down to the ones Direct and Composite actually scores. */
export function filterCompositeFactors(factors) {
  return (factors || []).filter((f) => !isExcludedCompositeFactor(f));
}

export function getOrderedCompositeFactors(sentimentScoreByFactor, productMappingAttribute) {
  return filterCompositeFactors(
    getOrderedAccessibilityFactors(sentimentScoreByFactor, productMappingAttribute)
  );
}

export const COMPOSITE_RBI_WEIGHTS = {
  visibility: 1 / 3,
  sentiment: 1 / 3,
  brandPillars: 1 / 3,
};


export const COMPOSITE_MAX = 10;


export function normalizeCompositeScores({ rank, favorability, brandPillarsAvg }) {
  const visNorm = Math.min(COMPOSITE_MAX, Math.max(0, (rank ?? 0) / 5));
  const sentNorm = Math.min(COMPOSITE_MAX, Math.max(0, (favorability ?? 0) / 5));
  const pillarsNorm = Math.min(COMPOSITE_MAX, Math.max(0, (brandPillarsAvg ?? 0) * 1));
  return { visNorm, sentNorm, pillarsNorm };
}


export function calcCompositeIndexScore({ rank, favorability, brandPillarsAvg }) {
  const { visNorm, sentNorm, pillarsNorm } = normalizeCompositeScores({ rank, favorability, brandPillarsAvg });

  const contributions = {
    visibility: visNorm * COMPOSITE_RBI_WEIGHTS.visibility,
    sentiment: sentNorm * COMPOSITE_RBI_WEIGHTS.sentiment,
    brandPillars: pillarsNorm * COMPOSITE_RBI_WEIGHTS.brandPillars,
  };

  const total = contributions.visibility + contributions.sentiment + contributions.brandPillars;

  return { total, visNorm, sentNorm, pillarsNorm, contributions };
}

/**
 * @param {string[]} factors - already-filtered factor names, e.g. via getOrderedCompositeFactors
 * @param {(factor: string) => number|null} getRankForFactor - that factor's own visibility/rank score (0-50)
 * @param {(factor: string) => number|null} getSentimentForFactor - that factor's sentiment score (0-50)
 * @param {(factor: string) => number|null} getPillarForFactor - that factor's own pillar score (0-10)
 */
export function calcMultiFactorCompositeScore({ factors, getRankForFactor, getSentimentForFactor, getPillarForFactor }) {
  const breakdown = factors.map((factor) => {
    const rank = getRankForFactor ? getRankForFactor(factor) : null;
    const sentiment = getSentimentForFactor ? getSentimentForFactor(factor) : null;
    const pillar = getPillarForFactor ? getPillarForFactor(factor) : null;
    const { total, visNorm, sentNorm, pillarsNorm, contributions } = calcCompositeIndexScore({
      rank, favorability: sentiment, brandPillarsAvg: pillar,
    });
    return {
      factor,
      perFactorMax: COMPOSITE_MAX,
      visRaw: rank,
      sentRaw: sentiment,
      pillarRaw: pillar,
      visNorm, sentNorm, pillarsNorm, contributions,
      total,
    };
  });


  const total = Math.round(breakdown.reduce((sum, f) => sum + f.total, 0));

  return { total, perFactorMax: COMPOSITE_MAX, breakdown };
}


export const DIRECT_RBI_WEIGHTS = {
  visibility: 1 / 3,
  sentiment: 1 / 3,
  brandPillars: 1 / 3,
};


export function normalizeDirectScores({ rank, favorability, brandPillarsAvg }) {
  const visNorm = Math.min(100, Math.max(0, (rank ?? 0) * 2));
  const sentNorm = Math.min(100, Math.max(0, (favorability ?? 0) * 2));
  const pillarsNorm = Math.min(100, Math.max(0, (brandPillarsAvg ?? 0) * 10));
  return { visNorm, sentNorm, pillarsNorm };
}


export function calcDirectIndexScore({ rank, favorability, brandPillarsAvg }) {
  const { visNorm, sentNorm, pillarsNorm } = normalizeDirectScores({ rank, favorability, brandPillarsAvg });

  const contributions = {
    visibility: visNorm * DIRECT_RBI_WEIGHTS.visibility,
    sentiment: sentNorm * DIRECT_RBI_WEIGHTS.sentiment,
    brandPillars: pillarsNorm * DIRECT_RBI_WEIGHTS.brandPillars,
  };

  const total = Math.round(contributions.visibility + contributions.sentiment + contributions.brandPillars);

  return { total, visNorm, sentNorm, pillarsNorm, contributions };
}

/** Filters a factor list down to just the "overall place to work" factor(s). */
export function filterDirectFactors(factors) {
  return (factors || []).filter((f) => isExcludedCompositeFactor(f));
}


export function averageDirectRankAcrossVariants(rankScoreByFactor, directFactorVariants) {
  const scores = (directFactorVariants || [])
    .map((factor) => rankScoreByFactor?.[factor]?.score)
    .filter((v) => v != null && !isNaN(v));
  if (!scores.length) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

/**

 * @param {Object<string, {score:number}>} rankScoreByFactor - e.g. rankRow?.rankScoreByFactor
 * @param {Object<string, {normalizedScore:number}>} sentimentScoreByFactor - e.g. getSentimentScoreByFactor(...)
 * @param {{name:string, score:number}[]} brandPillarsDetail - e.g. getBrandPillarsDetail(...)
 */
export function calcDirectIndexScoreForEntity({ rankScoreByFactor, sentimentScoreByFactor, brandPillarsDetail }) {
  const rankVariants = filterDirectFactors(Object.keys(rankScoreByFactor || {}));
  const sentimentFactor = filterDirectFactors(Object.keys(sentimentScoreByFactor || {}))[0] ?? null;
  const pillarFactor = filterDirectFactors((brandPillarsDetail || []).map((a) => a.name))[0] ?? null;

  const rank = averageDirectRankAcrossVariants(rankScoreByFactor, rankVariants);
  const favorability = sentimentFactor ? sentimentScoreByFactor?.[sentimentFactor]?.normalizedScore ?? null : null;
  const brandPillarsAvg = pillarFactor
    ? (brandPillarsDetail || []).find((a) => a.name === pillarFactor)?.score ?? null
    : null;

  const score = calcDirectIndexScore({ rank, favorability, brandPillarsAvg });
  const factorLabel = sentimentFactor ?? pillarFactor ?? rankVariants[0] ?? "Overall Place to Work";

  return {
    ...score,
    factorLabel,
    rankVariants,
    sentimentFactor,
    pillarFactor,
    visRaw: rank,
    sentRaw: favorability,
    pillarRaw: brandPillarsAvg,
  };
}

export function getSentimentScoreByFactor(brandName, favorabilities) {
  if (!Array.isArray(favorabilities) || !brandName) return null;
  const row = favorabilities.find(
    (f) => (f?.name || "").toLowerCase().trim() === brandName.toLowerCase().trim()
  );
  return row?.sentimentScoreByFactor || null;
}

/**
 * Given a brand name and core_attributes context value,
 * returns the average attribute score (0-10) for that brand, or null if unavailable.
 */
export function getBrandPillarsAvg(brandName, coreAttributes) {
  if (!coreAttributes?.products?.length || !brandName) return null;

  const product = coreAttributes.products.find(
    (p) => (p.brandName || p.name || "").toLowerCase().trim() === brandName.toLowerCase().trim()
  );
  if (!product?.attributes) return null;

  const scores = Object.values(product.attributes)
    .map((attr) => parseFloat(attr?.score))
    .filter((s) => !isNaN(s));

  if (!scores.length) return null;
  return scores.reduce((sum, s) => sum + s, 0) / scores.length;
}

/**
 * Given a brand name and category_dimensions context value,
 * returns the average purchase criteria rating (0-10) for that brand, or null if unavailable.
 */
export function getPurchaseCriteriaAvg(brandName, categoryDimensions) {
  if (!categoryDimensions?.data?.length || !brandName) return null;

  const ratings = [];
  for (const dimension of categoryDimensions.data) {
    const brandEntry = (dimension.brands || []).find(
      (b) => (b.name || "").toLowerCase().trim() === brandName.toLowerCase().trim()
    );
    const rating = parseFloat(brandEntry?.rating);
    if (!isNaN(rating)) ratings.push(rating);
  }

  if (!ratings.length) return null;
  return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
}

/**
 * Returns per-attribute scores for a brand from core_attributes.
 * Returns array of { name, score, definition, explanation } sorted by name.
 */
export function getBrandPillarsDetail(brandName, coreAttributes) {
  if (!coreAttributes?.products?.length || !brandName) return [];

  const product = coreAttributes.products.find(
    (p) => (p.brandName || p.name || "").toLowerCase().trim() === brandName.toLowerCase().trim()
  );
  if (!product?.attributes) return [];

  return Object.entries(product.attributes)
    .map(([name, attr]) => ({
      name,
      score: attr?.score != null ? parseFloat(attr.score) : null,
      definition: attr?.definition || "",
      explanation: attr?.explanation || "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * For product discovery type: returns average score (0-10) across all
 * taxonomy categories for a given product name.
 * taxonomies = discoveryCtx.taxonomies
 */
export function getProductDimensionsAvg(productName, taxonomies) {
  if (!taxonomies?.data?.products?.length || !taxonomies?.data?.categories?.length || !productName) return null;

  const product = taxonomies.data.products.find(
    (p) => (p.name || "").toLowerCase().trim() === productName.toLowerCase().trim()
  );
  if (!product?.scores) return null;

  const categoryAvgs = taxonomies.data.categories
    .map((cat) => {
      const subcategories = Object.values(product.scores?.[cat.name]?.subcategories || {});
      if (!subcategories.length) return null;
      const total = subcategories.reduce((acc, sub) => acc + (isNaN(+sub.score) ? 0 : +sub.score), 0);
      return total / subcategories.length;
    })
    .filter((v) => v != null);

  if (!categoryAvgs.length) return null;
  return categoryAvgs.reduce((a, b) => a + b, 0) / categoryAvgs.length;
}

/**
 * Returns per-dimension ratings for a brand from category_dimensions.
 * Returns array of { name, rating, explanation } sorted by name.
 */
export function getPurchaseCriteriaDetail(brandName, categoryDimensions) {
  if (!categoryDimensions?.data?.length || !brandName) return [];

  return categoryDimensions.data
    .map((dimension) => {
      const brandEntry = (dimension.brands || []).find(
        (b) => (b.name || "").toLowerCase().trim() === brandName.toLowerCase().trim()
      );
      return {
        name: dimension.name,
        rating: brandEntry?.rating != null ? parseFloat(brandEntry.rating) : null,
        explanation: brandEntry?.explanation || "",
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
